import React from 'react';
import { useNavigate } from 'react-router-dom';
import { INSPECTION_STAGES, INSPECTION_ITEMS, isItemApplicable, computeAggregation } from '@skd/shared';
import { Button } from '../components/ui/Button';
import { StatusChip } from '../components/ui/StatusChip';
import { PageHeader } from '../components/layout/PageHeader';
import { StatTile } from '../components/dashboard/StatTile';

interface RecordViewProps {
  record: any | null;
}

export function RecordView({ record }: RecordViewProps) {
  const navigate = useNavigate();

  if (!record) {
    return (
      <div className="card p-8 text-center">
        <p className="text-[0.875rem] text-[var(--text-muted)]">This record could not be found.</p>
        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const agg = computeAggregation(record.stages);

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      <PageHeader
        title={`Body ${record.metadata.body_number}`}
        meta={
          <>
            {record.defectCount === 0 ? (
              <StatusChip status="ok">Passed</StatusChip>
            ) : (
              <StatusChip status="ng">{record.verdict}</StatusChip>
            )}
            <span className="text-[var(--text-dim)]">·</span>
            <span>{record.inspectorName || 'Unassigned'}</span>
            <span className="text-[var(--text-dim)]">·</span>
            <span className="tnum">{record.updatedAt || record.createdAt || record.timestamp}</span>
          </>
        }
        actions={
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
            Back to Dashboard
          </Button>
        }
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Checked" value={agg.totalChecked} />
        <StatTile label="Passed" value={agg.passCount} />
        <StatTile
          label="Defects"
          value={agg.defectCount}
          chip={agg.defectCount > 0 ? <StatusChip status="ng">NG</StatusChip> : undefined}
        />
        <StatTile label="Pass rate" value={`${agg.passRate}%`} />
      </section>

      <section className="card p-4 sm:p-5">
        <h2 className="card-title">Vehicle &amp; container</h2>
        <dl className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-[0.75rem] text-[var(--text-dim)]">Model</dt>
            <dd className="mt-0.5 text-[0.875rem] font-medium">{record.metadata.model}</dd>
          </div>
          <div>
            <dt className="text-[0.75rem] text-[var(--text-dim)]">Invoice</dt>
            <dd className="mt-0.5 font-mono text-[0.875rem] font-medium">{record.metadata.invoice_no}</dd>
          </div>
          <div>
            <dt className="text-[0.75rem] text-[var(--text-dim)]">Container origin</dt>
            <dd className="mt-0.5 text-[0.875rem] font-medium">{record.metadata.container_origins}</dd>
          </div>
          <div>
            <dt className="text-[0.75rem] text-[var(--text-dim)]">Inside condition</dt>
            <dd className="mt-0.5 text-[0.875rem] font-medium">{record.metadata.inside_container}</dd>
          </div>
        </dl>
      </section>

      <div className="space-y-4">
          {INSPECTION_STAGES.map((stage) => {
            const sd = record.stages[stage.id];
            if (!sd) return null;
            return (
              <section key={stage.id} className="card p-4 sm:p-5">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="card-title">{stage.shortName}</h3>
                    {sd.saved ? (
                      <StatusChip status="ok">
                        Saved by {sd.savedBy || sd.inspectorFure} at {sd.savedAt || sd.dateTime}
                      </StatusChip>
                    ) : (
                      <StatusChip status="warning">Not saved</StatusChip>
                    )}
                  </div>
                  {sd.actionStatus === 'SKD Body OK' ? (
                    <StatusChip status="ok">{sd.actionStatus}</StatusChip>
                  ) : (
                    <StatusChip status="ng">{sd.actionStatus}</StatusChip>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {INSPECTION_ITEMS.filter((item) => isItemApplicable(item.id, stage.id)).map((item) => {
                    const itemData = sd.items[item.id];
                    if (!itemData) return null;
                    const isNG = itemData.status === 'NG';
                    return (
                      <div
                        key={item.id}
                        className={`rounded-[var(--radius)] border p-2.5 ${
                          isNG
                            ? 'border-[var(--status-ng-border)] bg-[var(--status-ng-bg)]'
                            : 'border-[var(--border-color)] bg-[var(--card-bg-subtle)]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="min-w-0">
                            <span className="flex items-baseline gap-1.5">
                              <span className="text-[0.6875rem] text-[var(--text-dim)]">({item.id})</span>
                              <span className="truncate text-[0.8125rem] font-medium">{item.code}</span>
                            </span>
                            <span className="mt-0.5 block text-[0.75rem] text-[var(--text-muted)]">{item.label}</span>
                          </span>
                          <span className="flex-shrink-0">
                            {isNG ? (
                              <StatusChip status="ng">NG</StatusChip>
                            ) : itemData.status === 'OK' ? (
                              <StatusChip status="ok">OK</StatusChip>
                            ) : (
                              <StatusChip status="neutral">N/A</StatusChip>
                            )}
                          </span>
                        </div>
                        {isNG && (
                          <div className="mt-2 border-t border-[var(--status-ng-border)] pt-2">
                            <div className="text-[0.75rem] font-medium text-[var(--status-ng-text)]">
                              {itemData.defectType || 'No defect type'}
                            </div>
                            {itemData.remark && (
                              <div className="mt-0.5 text-[0.75rem] italic text-[var(--text-dim)]">
                                &ldquo;{itemData.remark}&rdquo;
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
      </div>
    </div>
  );
}
